/**
 * LocalityContext — global source of truth for the user's active locality.
 *
 * Resolution order on load:
 *   1. If signed in AND user has `locality` on their record → use it.
 *   2. Else if localStorage has a locality → use that.
 *   3. Else attempt browser geolocation → reverse-geocode via our backend.
 *   4. Else null (component shows a "Set Your Location" prompt).
 *
 * Persistence:
 *   - Signed in → PATCH /api/locality/me
 *   - Signed out → localStorage
 */
import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { titleCaseFields } from "@/lib/titleCase";

export const LocalityContext = createContext(null);
const LS_KEY = "sp_locality";

export function LocalityProvider({ children }) {
  const { user } = useAuth();
  const [locality, setLocality] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ready | needs-setup

  // Load once when auth state resolves.
  useEffect(() => {
    let alive = true;
    async function boot() {
      // 1) Signed-in user record
      if (user?.locality) {
        if (alive) { setLocality(titleCaseFields(user.locality)); setStatus("ready"); }
        return;
      }
      // 2) localStorage
      try {
        const raw = localStorage.getItem(LS_KEY);
        if (raw) {
          const cached = JSON.parse(raw);
          if (cached?.city && cached?.country) {
            if (alive) { setLocality(titleCaseFields(cached)); setStatus("ready"); }
            return;
          }
        }
      } catch { /* ignore */ }
      // 3) Browser geolocation → reverse geocode
      if ("geolocation" in navigator) {
        const attempt = new Promise((resolve) => {
          navigator.geolocation.getCurrentPosition(
            (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
            () => resolve(null),
            { timeout: 6000, maximumAge: 5 * 60 * 1000 },
          );
        });
        const coords = await attempt;
        if (coords) {
          try {
            const { data } = await http.get(`/geocode/reverse?lat=${coords.lat}&lng=${coords.lng}`);
            if (alive && data?.city && data?.country) {
              const loc = titleCaseFields({ ...data, lat: coords.lat, lng: coords.lng });
              setLocality(loc);
              setStatus("ready");
              try { localStorage.setItem(LS_KEY, JSON.stringify(loc)); } catch {}
              return;
            }
          } catch {
            // Google not configured or failed → fall through to manual.
          }
        }
      }
      // 4) Give up → prompt user
      if (alive) setStatus("needs-setup");
    }
    boot();
    return () => { alive = false; };
  }, [user?.id, user?.locality]);

  const save = useCallback(async (loc) => {
    // Always normalize free-form user input to title-case before we
    // persist it — prevents "thiruvananthapuram" from leaking into
    // banners / user records / near-you feeds.
    const normalized = titleCaseFields(loc, ["city", "state", "country"]);
    setLocality(normalized);
    setStatus("ready");
    try { localStorage.setItem(LS_KEY, JSON.stringify(normalized)); } catch {}
    if (user) {
      try { await http.patch("/locality/me", normalized); } catch { /* non-fatal */ }
    }
    // Fire-and-forget: ask the backend to make sure the "Near You"
    // strips for this locality have content.
    if (normalized?.city && normalized?.country) {
      http.post("/local-content/kick", {
        city: normalized.city, state: normalized.state || null, country: normalized.country,
      }).catch(() => { /* non-fatal */ });
    }
  }, [user]);

  const clear = useCallback(() => {
    setLocality(null);
    setStatus("needs-setup");
    try { localStorage.removeItem(LS_KEY); } catch {}
  }, []);

  const value = useMemo(() => ({ locality, status, save, clear }), [locality, status, save, clear]);
  return <LocalityContext.Provider value={value}>{children}</LocalityContext.Provider>;
}

export function useLocality() {
  const ctx = useContext(LocalityContext);
  if (!ctx) throw new Error("useLocality must be used inside LocalityProvider");
  return ctx;
}
